import queryClient from '@src/app/providers/queryClient';
import type { APIClient } from '@src/shared/api';
import useAPIClient from '@src/shared/hooks/api/useAPIClient';
import { buildSectionCommitFormData } from '@src/shared/lib';
import { useMutation } from '@src/shared/query';
import type { AppQueryKey } from '@src/shared/query/utils';
import {
  type InfiniteData,
  keepPreviousData,
  queryOptions,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';

import {
  contactCreatePayloadToDto,
  contactDetailFromDto,
  contactListItemFromDto,
  contactMatchFromDto,
} from '../../lib';
import type {
  CommitContactDetailPayload,
  CommitContactDetailResponse,
  ContactCreatePayload,
  ContactDetail,
  ContactDetailDto,
  ContactFilterOptions,
  ContactListFilterParams,
  ContactListItem,
  ContactListItemDto,
  ContactListOrdering,
  ContactListResponse,
  ContactListResponseDto,
  ContactMatch,
  ContactMatchDto,
  ContactSummary,
} from '../../types';
import {
  clientContactFilterOptionsUrl,
  clientContactUrl,
  clientContactMatchesUrl,
  clientContactSummaryUrl,
  clientContactsUrl,
} from './constants';
import { CLIENTS_KEY, clientContactDetailKey, invalidateClients } from './keys';

const CONTACTS_KEY: AppQueryKey = [...CLIENTS_KEY, 'contacts'];

type UseClientContactsParams = {
  pageSize: number;
  ordering?: ContactListOrdering | `-${ContactListOrdering}`;
  filterParams: ContactListFilterParams;
  enabled?: boolean;
};

/** Client contacts list, one page at a time. */
export function useClientContacts({
  pageSize,
  ordering,
  filterParams,
  enabled = true,
}: UseClientContactsParams) {
  const apiClient = useAPIClient();
  return useInfiniteQuery({
    queryKey: [...CONTACTS_KEY, 'list', pageSize, ordering, filterParams],
    enabled,
    placeholderData: keepPreviousData,
    initialPageParam: 1,
    queryFn: ({ pageParam, signal }) =>
      apiClient.request({
        url: clientContactsUrl,
        payload: {
          ...filterParams,
          page: String(pageParam),
          page_size: String(pageSize),
          ...(ordering ? { ordering } : {}),
        },
        options: { signal },
      }) as Promise<ContactListResponseDto>,
    select: (data): InfiniteData<ContactListResponse> => ({
      ...data,
      pages: data.pages.map((page) => ({
        ...page,
        results: page.results.map(contactListItemFromDto),
      })),
    }),
    getNextPageParam: (lastPage) =>
      lastPage.meta.next ? lastPage.meta.page + 1 : undefined,
  });
}

/** Total, current, obsolete and inactive contacts the user may view. */
export function useClientContactSummary() {
  const apiClient = useAPIClient();
  return useQuery({
    queryKey: [...CONTACTS_KEY, 'summary'],
    queryFn: ({ signal }) =>
      apiClient.request({
        url: clientContactSummaryUrl,
        options: { signal },
      }) as Promise<ContactSummary>,
  });
}

/** Values of the Job and Current client filters of the list. */
export function useClientContactFilterOptions() {
  const apiClient = useAPIClient();
  return useQuery({
    queryKey: [...CONTACTS_KEY, 'filter-options'],
    queryFn: ({ signal }) =>
      apiClient.request({
        url: clientContactFilterOptionsUrl,
        options: { signal },
      }) as Promise<ContactFilterOptions>,
  });
}

/**
 * Existing contacts that may be the person being added (Add Client Contact
 * wizard): family name starting with the typed one, narrowed by the first
 * name once typed. Runs from MATCH_MIN_FAMILY_NAME letters of the family
 * name; debounce the typed values.
 */
export const MATCH_MIN_FAMILY_NAME = 3;

export function useClientContactMatches({
  familyName,
  firstName,
}: {
  familyName: string;
  firstName: string;
}) {
  const apiClient = useAPIClient();
  const payload = {
    family_name: familyName.trim(),
    first_name: firstName.trim(),
  };
  const enabled = payload.family_name.length >= MATCH_MIN_FAMILY_NAME;
  return useQuery({
    queryKey: [...CONTACTS_KEY, 'matches', payload],
    enabled,
    // Without the guard a too short name would keep showing old matches.
    placeholderData: enabled ? keepPreviousData : undefined,
    queryFn: ({ signal }) =>
      apiClient.request({
        url: clientContactMatchesUrl,
        payload,
        options: { signal },
      }) as Promise<ContactMatchDto[]>,
    select: (data): ContactMatch[] => data.map(contactMatchFromDto),
  });
}

/**
 * Add Client Contact wizard: a new contact with its first position, or a
 * new position of an existing contact. Returns the contact as a list row.
 */
export function useCreateClientContact() {
  return useMutation<ContactListItem, Error, ContactCreatePayload>({
    mutationKey: [...CONTACTS_KEY, 'create'],
    mutateFn: async ({ apiClient, ...payload }) =>
      contactListItemFromDto(
        (await apiClient.request({
          url: clientContactsUrl,
          method: 'POST',
          payload: contactCreatePayloadToDto(payload),
        })) as ContactListItemDto,
      ),
    onSuccess: () => {
      void invalidateClients();
    },
  });
}

function contactDetailQueryOptions(apiClient: APIClient, id: number | string) {
  return queryOptions({
    queryKey: clientContactDetailKey(id),
    queryFn: ({ signal }) =>
      apiClient.request({
        url: clientContactUrl(id),
        options: { signal },
      }) as Promise<ContactDetailDto>,
  });
}

/** Header and sections of the client contact page. */
export function useClientContactDetail(
  id: number | string,
  { enabled = true }: { enabled?: boolean } = {},
) {
  const apiClient = useAPIClient();
  return useQuery({
    ...contactDetailQueryOptions(apiClient, id),
    enabled,
    select: (data): ContactDetail => contactDetailFromDto(data),
  });
}

/**
 * The contact of the contact page outside React, e.g. in a route loader.
 * Shares the cache with `useClientContactDetail`.
 */
export async function fetchClientContactDetail(
  apiClient: APIClient,
  id: number | string,
): Promise<ContactDetail> {
  const data = await queryClient.ensureQueryData(
    contactDetailQueryOptions(apiClient, id),
  );
  return contactDetailFromDto(data);
}

/** Saves the given sections of the contact page together. */
export function useCommitClientContactDetail() {
  return useMutation<
    CommitContactDetailResponse,
    Error,
    { id: number | string; sections: CommitContactDetailPayload }
  >({
    mutationKey: [...CONTACTS_KEY, 'detail', 'commit'],
    mutateFn: ({ apiClient, id, sections }) =>
      apiClient.request({
        url: `${clientContactUrl(id)}detail/commit/`,
        method: 'PATCH',
        payload: buildSectionCommitFormData(sections),
        contentType: 'multipart/form-data',
      }) as Promise<CommitContactDetailResponse>,
    onSuccess: (response, variables) => {
      // Every saved section answers with the whole contact; it goes into
      // the cache at once, so the page does not show the old one meanwhile.
      const saved = Object.values(response.results).find(Boolean);
      if (saved) {
        queryClient.setQueryData(clientContactDetailKey(variables.id), saved);
      }
      void invalidateClients();
    },
  });
}
